import React, { useContext, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import Link from 'next/link';
import Button from '@/components/ui/Button';
import Label, { LabelType } from '@/components/ui/Label';
import { MergedSingleDrop } from '@/types/singleDrop';
import { toggleVideo } from '@/lib/utils/helpers';
import { ROUTE } from '@/lib/constants';
import styles from './DropCard.module.css';
import Media from '@/components/ui/Media/Media';
import { XAContext } from '@/providers/XAProvider';

type DropCardProps = {
  singleDrop: MergedSingleDrop;
  isLoading?: boolean;
};

export default function DropCard(
  {
    singleDrop,
    isLoading,
  }: DropCardProps,
): React.ReactElement {
  const xa = useContext(XAContext);

  const videoRef = useRef<HTMLVideoElement>(null);

  const isExpired = useMemo(() => singleDrop.slug === 'holiday' || singleDrop.is_expired, []);

  const getBottomContent = () => {
    if (isLoading) {
      return null;
    }

    if (singleDrop.date_claim) {
      return (
        <Label>
          <FormattedMessage id="drop_card.collected_label" />
        </Label>
      );
    }

    if (isExpired) {
      return (
        <Label type={LabelType.EXPIRED}>
          EXPIRED
        </Label>
      );
    }

    return (
      <Button variant="outlined" color="primary">
        <FormattedMessage id="drop_card.claim_button" />
      </Button>
    );
  };

  const getCardContent = () => (
    <>
      <div
        className={classNames(
          styles['drop-card__image-container'],
          { 'card-loader': isLoading },
        )}
      >
        {!isLoading && <Media media_url={singleDrop.image_url} ref={videoRef} className={styles['drop-card__image']} />}
      </div>
      <div className={styles['drop-card__content-wrapper']}>
        <div className={styles['drop-card__text-container']}>
          <h4
            className={classNames(
              styles['drop-card__title'],
              { [styles['drop-card--content-loading']]: isLoading },
              { 'card-loader': isLoading },
            )}
          >
            {!isLoading && singleDrop.title}
          </h4>
          <p
            className={classNames(
              styles['drop-card__description'],
              { [styles['drop-card--content-loading']]: isLoading },
              { 'card-loader': isLoading },
            )}
            dangerouslySetInnerHTML={{
              __html: !isLoading ? singleDrop.description || '' : '',
            }}
          />
        </div>
        <div className={styles['drop-card__bottom-container']}>
          {getBottomContent()}
        </div>
      </div>
    </>
  );

  const wrapToLink = () => {
    const dropExternalLink = `https://x.la/${singleDrop.slug}`;
    const dropInternalLink = `${ROUTE.DROP_ROUTE}/${singleDrop.id}`;

    if (singleDrop.date_claim) {
      return (
        <Link href={dropInternalLink}>
          {getCardContent()}
        </Link>
      );
    }

    if (isExpired) {
      return getCardContent();
    }

    return (
      <a
        href={dropExternalLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          xa.hit(dropExternalLink);
        }}
      >
        {getCardContent()}
      </a>
    );
  };

  return (
    <div
      className={classNames(
        styles['drop-card'],
        { [styles['drop-card--active']]: !isExpired },
      )}
      onMouseEnter={() => toggleVideo(videoRef, true)}
      onMouseLeave={() => toggleVideo(videoRef, false)}
    >
      {wrapToLink()}
    </div>
  );
}
